import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import API_URL from '../../ServiceModule/Service';

export const mergeGuestCart = createAsyncThunk(
  'guestCart/mergeGuestCart',
  async ({ user_id, event_id, items, promoCode }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${API_URL}/cart/guest/merge`,
        { user_id, event_id, items, promoCode },
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const getActiveEventId = () => {
  if (typeof window === 'undefined') return null;
  const pathParts = window.location.pathname.split('/');
  // Path format: /events/:eventId/...
  if (
    pathParts[1] === 'events' &&
    pathParts[2] &&
    pathParts[2] !== 'terms-and-conditions' &&
    pathParts[2] !== 'privacy-policy' &&
    pathParts[2] !== 'contact'
  ) {
    return pathParts[2];
  }
  return null;
};

const loadGuestItems = () => {
  if (typeof window === 'undefined') return [];
  try {
    const eventId = getActiveEventId();
    if (!eventId) return [];
    const serialized = localStorage.getItem(`guest_cart_items_${eventId}`);
    if (!serialized) return [];
    const parsed = JSON.parse(serialized);
    return parsed && Array.isArray(parsed.items) ? parsed.items : [];
  } catch (err) {
    console.error('Error loading guest cart:', err);
    return [];
  }
};

const loadGuestPromo = () => {
  if (typeof window === 'undefined') return '';
  try {
    const eventId = getActiveEventId();
    if (!eventId) return '';
    const serialized = localStorage.getItem(`guest_cart_items_${eventId}`);
    if (!serialized) return '';
    const parsed = JSON.parse(serialized);
    return parsed && parsed.promoCode ? parsed.promoCode : '';
  } catch (err) {
    console.error('Error loading guest promo:', err);
    return '';
  }
};

const saveGuestItems = (items, promoCode) => {
  if (typeof window === 'undefined') return;
  try {
    const eventId = getActiveEventId();
    if (!eventId) return;
    const rawItems = items.map((item) => ({
      product_id: item.product_id,
      sku_id: item.sku_id,
      quantity: item.quantity,
    }));
    let finalPromo = promoCode;
    if (promoCode === undefined || promoCode === null) {
      const serialized = localStorage.getItem(`guest_cart_items_${eventId}`);
      if (serialized) {
        const parsed = JSON.parse(serialized);
        finalPromo = parsed?.promoCode || '';
      }
    }
    localStorage.setItem(
      `guest_cart_items_${eventId}`,
      JSON.stringify({ items: rawItems, promoCode: finalPromo })
    );
  } catch (err) {
    console.error('Error saving guest cart:', err);
  }
};

const calculateTotals = (items) => {
  const totalQuantity = items.reduce((sum, item) => sum + (item.quantity || 0), 0);
  return { totalQuantity };
};

const initialItems = loadGuestItems();
const initialTotals = calculateTotals(initialItems);

const initialState = {
  items: initialItems,
  totalQuantity: initialTotals.totalQuantity,
  promoCode: loadGuestPromo(),
};

const guestCartSlice = createSlice({
  name: 'guestCart',
  initialState,
  reducers: {
    addGuestItem: (state, action) => {
      const newItem = action.payload;
      if (!newItem || !newItem.sku_id) return;

      const existingIndex = state.items.findIndex(
        (item) => item.sku_id === newItem.sku_id && item.product_id === newItem.product_id
      );

      const qtyToAdd = newItem.quantity !== undefined ? Number(newItem.quantity) : 1;

      if (existingIndex > -1) {
        state.items[existingIndex].quantity += qtyToAdd;
        const price = Number(state.items[existingIndex].price) || 0;
        state.items[existingIndex].total_price = (
          state.items[existingIndex].quantity * price
        ).toFixed(2);
      } else {
        const price = Number(newItem.price) || 0;
        state.items.push({
          ...newItem,
          quantity: qtyToAdd,
          price: price,
          total_price: (qtyToAdd * price).toFixed(2),
        });
      }

      state.totalQuantity = calculateTotals(state.items).totalQuantity;
      saveGuestItems(state.items);
    },

    decreaseGuestItem: (state, action) => {
      const payload = action.payload;
      if (!payload || !payload.sku_id) return;

      const existingIndex = state.items.findIndex(
        (item) => item.sku_id === payload.sku_id && item.product_id === payload.product_id
      );

      if (existingIndex > -1) {
        const item = state.items[existingIndex];
        if (item.quantity > 1) {
          item.quantity -= 1;
          const price = Number(item.price) || 0;
          item.total_price = (item.quantity * price).toFixed(2);
        } else {
          state.items.splice(existingIndex, 1);
        }
      }

      state.totalQuantity = calculateTotals(state.items).totalQuantity;
      saveGuestItems(state.items);
    },

    removeGuestItem: (state, action) => {
      const payload = action.payload;
      if (!payload || !payload.sku_id) return;

      state.items = state.items.filter(
        (item) => !(item.sku_id === payload.sku_id && item.product_id === payload.product_id)
      );

      state.totalQuantity = calculateTotals(state.items).totalQuantity;
      saveGuestItems(state.items);
    },

    clearGuestCart: (state) => {
      state.items = [];
      state.totalQuantity = 0;
      saveGuestItems([], '');
      state.promoCode = '';
    },

    setGuestPromoCode: (state, action) => {
      state.promoCode = action.payload || '';
      saveGuestItems(state.items, action.payload || '');
    },

    clearGuestPromoCode: (state) => {
      state.promoCode = '';
      saveGuestItems(state.items, '');
    },

    setGuestCart: (state, action) => {
      const items = action.payload || [];
      state.items = items;
      state.totalQuantity = calculateTotals(items).totalQuantity;
    },

    setGuestItemQuantity: (state, action) => {
      const newItem = action.payload;
      if (!newItem || !newItem.sku_id) return;

      const existingIndex = state.items.findIndex(
        (item) => item.sku_id === newItem.sku_id && item.product_id === newItem.product_id
      );

      const targetQty = Number(newItem.quantity) || 0;

      if (targetQty <= 0) {
        state.items = state.items.filter(
          (item) => !(item.sku_id === newItem.sku_id && item.product_id === newItem.product_id)
        );
      } else {
        if (existingIndex > -1) {
          state.items[existingIndex].quantity = targetQty;
          const price = Number(state.items[existingIndex].price) || 0;
          state.items[existingIndex].total_price = (targetQty * price).toFixed(2);
        } else {
          const price = Number(newItem.price) || 0;
          state.items.push({
            ...newItem,
            quantity: targetQty,
            price: price,
            total_price: (targetQty * price).toFixed(2),
          });
        }
      }

      state.totalQuantity = calculateTotals(state.items).totalQuantity;
      saveGuestItems(state.items);
    },

    adjustGuestItemsQuantities: (state, action) => {
      const updates = action.payload || [];
      updates.forEach((update) => {
        const { product_id, sku_id, quantity, price } = update;
        const existingIndex = state.items.findIndex(
          (item) => item.sku_id === sku_id && item.product_id === product_id
        );

        if (existingIndex > -1) {
          state.items[existingIndex].quantity += quantity;
          if (state.items[existingIndex].quantity <= 0) {
            state.items.splice(existingIndex, 1);
          } else {
            const currentPrice = Number(state.items[existingIndex].price) || 0;
            state.items[existingIndex].total_price = (
              state.items[existingIndex].quantity * currentPrice
            ).toFixed(2);
          }
        } else if (quantity > 0) {
          const itemPrice = Number(price) || 0;
          state.items.push({
            product_id,
            sku_id,
            quantity,
            price: itemPrice,
            total_price: (quantity * itemPrice).toFixed(2),
          });
        }
      });

      state.totalQuantity = calculateTotals(state.items).totalQuantity;
      saveGuestItems(state.items);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(mergeGuestCart.fulfilled, (state) => {
      state.items = [];
      state.totalQuantity = 0;
      state.promoCode = '';
      if (typeof window !== 'undefined') {
        try {
          const eventId = getActiveEventId();
          if (eventId) {
            localStorage.removeItem(`guest_cart_items_${eventId}`);
          }
        } catch (e) {
          console.error(e);
        }
      }
    });
  },
});

export const {
  addGuestItem,
  decreaseGuestItem,
  removeGuestItem,
  clearGuestCart,
  setGuestCart,
  setGuestItemQuantity,
  adjustGuestItemsQuantities,
  setGuestPromoCode,
  clearGuestPromoCode,
} = guestCartSlice.actions;

export default guestCartSlice.reducer;
